class ProductInformation extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this._boundHandleVariantEvent = this.handleVariantEvent.bind(this);
    this.querySelector('pinfo-variant-picker')?.addEventListener(
      'input',
      this._boundHandleVariantEvent
    );
    this.initQuantitySelector();
    this.initVariantPickerTapFix();
    this.syncVariantPickerState();
    this.initSwiper();
    this._galleryWasMobile = this.isMobileProductViewport();
    this._boundGalleryResize = this.handleGalleryResize.bind(this);
    window.addEventListener('resize', this._boundGalleryResize);
    this.initTrustBadgeSwiper();
    this.initSellingPlan();
    this.initAccordion();
    this.bindFormSubmit();
    this.bindBuyNow();
  }

  disconnectedCallback() {
    const picker = this.querySelector('pinfo-variant-picker');
    if (picker && this._boundHandleVariantEvent) {
      picker.removeEventListener('input', this._boundHandleVariantEvent);
    }

    if (this._boundGalleryResize) {
      window.removeEventListener('resize', this._boundGalleryResize);
    }
  }

  handleGalleryResize() {
    window.clearTimeout(this._galleryResizeTimer);
    this._galleryResizeTimer = window.setTimeout(() => {
      const isMobile = this.isMobileProductViewport();

      if (this._galleryWasMobile === undefined) {
        this._galleryWasMobile = isMobile;
        return;
      }

      if (this._galleryWasMobile !== isMobile) {
        this._galleryWasMobile = isMobile;
        this.initSwiper();
      }
    }, 150);
  }

  // product | json — used for media, variant picker, price block
  get productJSON() {
    if (!this._productJSON) {
      const el = this.querySelector('script[data-product-json]');
      this._productJSON = el ? JSON.parse(el.textContent) : null;
    }
    return this._productJSON;
  }

  get spAllocations() {
    if (!this._spAllocations) {
      const product = this.productJSON;
      if (product?.variants) {
        const variants = {};
        product.variants.forEach(v => {
          const allocations = {};
          (v.selling_plan_allocations || []).forEach(a => {
            allocations[String(a.selling_plan_id)] = {
              price: a.price,
              compare_at_price: a.compare_at_price
            };
          });
          variants[String(v.id)] = { price: v.price, allocations };
        });
        this._spAllocations = { variants };
      } else {
        const el = document.getElementById('sp-allocations-json');
        try {
          this._spAllocations = el ? JSON.parse(el.textContent) : { variants: {} };
        } catch (e) {
          console.warn('sp-allocations-json parse error', e);
          this._spAllocations = { variants: {} };
        }
      }
    }
    return this._spAllocations;
  }

  isMobileProductViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  getVariantOptionGroups() {
    return Array.from(
      this.querySelectorAll('pinfo-variant-picker .pinfo-variants-options')
    ).sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
  }

  getSelectedOptionValues() {
    return this.getVariantOptionGroups().map((group) => {
      return group.querySelector('input[type="radio"]:checked')?.value || null;
    });
  }

  findVariantByOptions(optionValues) {
    if (!Array.isArray(optionValues) || optionValues.some((value) => value == null)) return null;

    return this.productJSON?.variants?.find((variant) => {
      return variant.options?.length === optionValues.length
        && variant.options.every((value, index) => value === optionValues[index]);
    }) || null;
  }

  getVariantUrl(variant) {
    if (!variant?.id) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  findBestAvailableVariant(changedOptionIndex, changedValue, selectedValues) {
    const variants = this.productJSON?.variants || [];
    const candidates = variants.filter((variant) => {
      return variant.available && variant.options?.[changedOptionIndex] === changedValue;
    });

    if (!candidates.length) return null;

    return candidates.sort((a, b) => {
      const score = (variant) => variant.options.reduce((total, value, index) => {
        if (index === changedOptionIndex) return total;
        return total + (value === selectedValues[index] ? 1 : 0);
      }, 0);

      return score(b) - score(a);
    })[0];
  }

  applyVariantOptions(variant) {
    if (!variant?.options) return;

    this.getVariantOptionGroups().forEach((group, index) => {
      const input = Array.from(group.querySelectorAll('input[type="radio"]'))
        .find((candidate) => candidate.value === variant.options[index]);

      if (input) input.checked = true;
    });
  }

  syncOptionAvailability(selectedValues = this.getSelectedOptionValues()) {
    const variants = this.productJSON?.variants || [];

    this.getVariantOptionGroups().forEach((group, optionIndex) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const isAvailable = variants.some((variant) => {
          if (!variant.available || variant.options?.[optionIndex] !== input.value) return false;

          return selectedValues.every((selectedValue, index) => {
            return index >= optionIndex || selectedValue == null || variant.options[index] === selectedValue;
          });
        });

        const card = input.closest('.pinfo-option-value');
        const label = input.labels?.[0] || card?.querySelector('label.size-card-label');

        input.disabled = !isAvailable;
        card?.classList.toggle('disabled-option', !isAvailable);
        label?.classList.toggle('disabled', !isAvailable);
        label?.setAttribute('aria-disabled', String(!isAvailable));
      });
    });
  }

  syncVariantPickerState() {
    const selectedValues = this.getSelectedOptionValues();
    this.syncOptionAvailability(selectedValues);
  }

  selectVariantInput(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled || input.checked) return;

    input.checked = true;
    input.blur?.();
    input.dispatchEvent(new Event('input', { bubbles: true }));

    // The mobile tap handler prevents the browser's native click/change sequence.
    // Notify integrations such as SellEasy after the variant state is updated.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updatePriceForVariant(variant) {
    if (!variant) return;

    const price = this.formatMoney(variant.price);
    const compareAtPrice = variant.compare_at_price && variant.compare_at_price > variant.price
      ? this.formatMoney(variant.compare_at_price)
      : '';

    this.querySelectorAll('.pinfo-price-wrapper .pinfo-price').forEach((priceEl) => {
      if (compareAtPrice) {
        priceEl.innerHTML = `
          <span class="pinfo-regular-price sale-price">${price}</span>
          <span class="pinfo-compare-price"><s>${compareAtPrice}</s></span>
        `;
      } else {
        priceEl.innerHTML = `<span class="pinfo-regular-price">${price}</span>`;
      }
    });
  }

  updateFormForVariant(variant) {
    if (!variant) return;

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = variant.id;
    });

    const addButton = this.querySelector('.product-form__submit.pinfo-add-to-cart');
    const addButtonText = addButton?.querySelector('.atc-text');
    const buyNowButton = this.querySelector('.p-info-btn-buy-now');
    const isAvailable = Boolean(variant.available);

    if (addButton) {
      if (!addButton.dataset.defaultText && addButtonText?.textContent?.trim() && addButtonText.textContent.trim() !== 'Sold Out') {
        addButton.dataset.defaultText = addButtonText.textContent.trim();
      }

      addButton.disabled = !isAvailable;
      addButton.classList.toggle('sold-out', !isAvailable);

      if (addButtonText) {
        addButtonText.textContent = isAvailable ? (addButton.dataset.defaultText || 'Add To Cart') : 'Sold Out';
      }
    }

    if (buyNowButton) {
      buyNowButton.disabled = !isAvailable;
    }
  }

  updateVariantClientSide(variant, url, options = {}) {
    if (!variant) return false;

    const { slideMedia = true } = options;

    this.updatePriceForVariant(variant);
    this.updateFormForVariant(variant);
    this.updateSellingPlanPrices(variant.id);
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variantId: variant.id, variant }
    }));

    if (slideMedia && !this.isMobileProductViewport()) {
      this.slideToVariantMedia(variant.id, 300);
    }

    this.updateURL(url);
    return true;
  }

  get mediaOrder() {
    if (!this._mediaOrder) {
      this._mediaOrder = Array.from(
        this.querySelectorAll('.main-swiper .swiper-slide')
      ).map(s => s.dataset.media);
    }
    return this._mediaOrder;
  }

  initTrustBadgeSwiper() {
    const container = this.querySelector('.pinfo-trust-badges-swiper');
    if (!container || !window.Swiper || this.trustBadgeSwiper) return;

    this.trustBadgeSwiper = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 30,
      loop: true,
      freeMode: false,
      allowTouchMove: false,
      speed: 5000, // higher = slower smooth scroll
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },
    });
  }

  initVariantPickerTapFix() {
    const picker = this.querySelector('pinfo-variant-picker');
    if (!picker || picker.dataset.mobileTapFix === 'true') return;
    picker.dataset.mobileTapFix = 'true';

    let touchStartX = 0;
    let touchStartY = 0;
    let touchMoved = false;
    let lastTouchSelectionAt = 0;
    const movementThreshold = 12;

    picker.addEventListener('touchstart', (event) => {
      if (!this.isMobileProductViewport() || event.touches.length !== 1) return;

      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchMoved = false;
    }, { capture: true, passive: true });

    picker.addEventListener('touchmove', (event) => {
      if (!this.isMobileProductViewport() || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);

      if (deltaX > movementThreshold || deltaY > movementThreshold) {
        touchMoved = true;
      }
    }, { capture: true, passive: true });

    picker.addEventListener('touchend', (event) => {
      if (!this.isMobileProductViewport()) return;

      const label = event.target?.closest?.('label.size-card-label');
      if (!label || touchMoved) return;

      const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
      if (!input || input.disabled || input.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      lastTouchSelectionAt = Date.now();
      this.selectVariantInput(input);
    }, { capture: true, passive: false });

    picker.addEventListener('click', (event) => {
      if (!this.isMobileProductViewport()) return;

      // Ignore the synthetic click that mobile browsers fire after touchend.
      if (Date.now() - lastTouchSelectionAt < 500) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const label = event.target?.closest?.('label.size-card-label');
      if (!label) return;

      const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
      if (!input || input.disabled || input.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.selectVariantInput(input);
    }, true);
  }

  handleVariantEvent(e) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const group = target.closest('.pinfo-variants-options');
    const changedOptionIndex = Number(group?.dataset.optionIndex);
    if (!Number.isInteger(changedOptionIndex)) return;

    target.blur();

    let selectedValues = this.getSelectedOptionValues();
    let variant = this.findVariantByOptions(selectedValues);

    if (!variant?.available) {
      const fallbackVariant = this.findBestAvailableVariant(
        changedOptionIndex,
        target.value,
        selectedValues
      );

      if (fallbackVariant) {
        variant = fallbackVariant;
        this.applyVariantOptions(variant);
        selectedValues = [...variant.options];
      }
    }

    this.syncOptionAvailability(selectedValues);

    if (!variant) {
      this.updateFormForVariant({ id: '', available: false });
      return;
    }

    const variantUrl = this.getVariantUrl(variant);
    if (!variantUrl) return;

    // Resolve and update the exact variant client-side on every device.
    // Mobile keeps its no-jump behavior and existing touch handling; desktop also
    // updates the product media without fetching and re-rendering the section.
    this.updateVariantClientSide(variant, variantUrl, {
      slideMedia: !this.isMobileProductViewport()
    });
  }

  slideToVariantMedia(variantId, speed = 300) {
    const product = this.productJSON;
    if (!product) return;

    const variant = product.variants.find(v => Number(v.id) === Number(variantId));
    if (!variant || !variant.featured_media) return;

    const mediaId = String(variant.featured_media.id);
    const idx = this.mediaOrder.indexOf(mediaId);

    if (idx > -1 && this.mainSwiper) {
      this.mainSwiper.slideTo(idx, speed);
    }
  }

  // ─── Quantity Selector ───────────────────────────────────────────
  initQuantitySelector() {
    const wrapper = this.querySelector('[data-quantity-selector]');
    if (!wrapper) return;
    const input = wrapper.querySelector('.qty-input');
    const dec = wrapper.querySelector('.decrease');
    const inc = wrapper.querySelector('.increase');
    dec?.addEventListener('click', () => {
      const currentValue = parseInt(input.value, 10) || 1;
      const minimumValue = parseInt(input.min, 10) || 1;
      if (currentValue > minimumValue) input.value = currentValue - 1;
    });
    inc?.addEventListener('click', () => {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    });
    input?.addEventListener('input', () => {
      if ((parseInt(input.value, 10) || 0) < 1) input.value = 1;
    });
  }

  // ─── Product Gallery ──────────────────────────────────────────────
  initSwiper() {
    const container = this.querySelector('.pdp-media-container');
    const mainEl = container?.querySelector('.main-swiper');
    const thumbEl = container?.querySelector('.thumb-swiper');

    if (!container || !mainEl || !window.Swiper) return;

    this.mainSwiper?.destroy(true, true);
    this.thumbSwiper?.destroy(true, true);
    this.mainSwiper = null;
    this.thumbSwiper = null;

    const isMobile = this.isMobileProductViewport();
    const useDesktopSideThumbs =
      !isMobile &&
      container.dataset.desktopGalleryNavigation === 'side_thumbnails';

    mainEl.style.touchAction = isMobile ? 'pan-y pinch-zoom' : '';

    if (thumbEl) {
      this.thumbSwiper = new Swiper(thumbEl, {
        slidesPerView: 'auto',
        spaceBetween: isMobile ? 8 : 10,
        watchSlidesProgress: true,
        freeMode: true,
        slideToClickedSlide: true,
        direction: useDesktopSideThumbs ? 'vertical' : 'horizontal',
      });

      const thumbWrapper = container.querySelector('.thumb-swiper-wrapper');
      const prevBtn = thumbWrapper?.querySelector('.thumb-nav-prev');
      const nextBtn = thumbWrapper?.querySelector('.thumb-nav-next');

      if (useDesktopSideThumbs) {
        prevBtn?.addEventListener('click', () => this.thumbSwiper?.slidePrev(200));
        nextBtn?.addEventListener('click', () => this.thumbSwiper?.slideNext(200));
        this._thumbPrevBtn = prevBtn;
        this._thumbNextBtn = nextBtn;
      } else {
        this._thumbPrevBtn = null;
        this._thumbNextBtn = null;
      }

      this.thumbSwiper.on('slideChange resize', () => this.updateThumbNavButtons());
      this.updateThumbNavButtons();
    }

    this.mainSwiper = new Swiper(mainEl, {
      loop: false,
      slidesPerView: 1,
      touchStartPreventDefault: false,
      thumbs: this.thumbSwiper ? { swiper: this.thumbSwiper } : undefined,
      on: {
        slideChange: () => {
          this.resetExternalVideoInteraction();
          this.updateMainNavButtons();
          this.syncThumbSwiperToMain(220);
        },
        init: () => {
          this.updateMainNavButtons();
          window.requestAnimationFrame(() => this.syncThumbSwiperToMain(0));
        },
      },
    });

    const prevArrow = mainEl.querySelector('.swiper-nav-prev');
    const nextArrow = mainEl.querySelector('.swiper-nav-next');

    prevArrow?.addEventListener('click', () => this.mainSwiper?.slidePrev(300));
    nextArrow?.addEventListener('click', () => this.mainSwiper?.slideNext(300));

    this._prevBtn = prevArrow;
    this._nextBtn = nextArrow;

    this.initExternalVideoSwipeGuard();
  }

  initExternalVideoSwipeGuard() {
    if (!this.isMobileProductViewport()) return;

    this.querySelectorAll('.main-swiper .pdp-media-external-video').forEach((video) => {
      if (video.dataset.swipeGuardInitialized === 'true') return;
      video.dataset.swipeGuardInitialized = 'true';

      video.addEventListener('click', () => {
        video.classList.add('is-video-interactive');
      });
    });
  }

  resetExternalVideoInteraction() {
    this.querySelectorAll('.main-swiper .pdp-media-external-video.is-video-interactive')
      .forEach((video) => video.classList.remove('is-video-interactive'));
  }

  syncThumbSwiperToMain(speed = 200) {
    if (!this.mainSwiper || !this.thumbSwiper) return;

    const activeIndex = Number.isFinite(this.mainSwiper.realIndex)
      ? this.mainSwiper.realIndex
      : this.mainSwiper.activeIndex;

    if (!Number.isFinite(activeIndex) || activeIndex < 0) return;

    this.thumbSwiper.slideTo(activeIndex, speed);
    this.updateThumbNavButtons();
  }

  updateMainNavButtons() {
    if (!this.mainSwiper) return;

    this._prevBtn?.classList.toggle('swiper-button-disabled', this.mainSwiper.isBeginning);
    this._nextBtn?.classList.toggle('swiper-button-disabled', this.mainSwiper.isEnd);
  }

  updateThumbNavButtons() {
    if (!this.thumbSwiper) return;

    this._thumbPrevBtn?.classList.toggle('swiper-button-disabled', this.thumbSwiper.isBeginning);
    this._thumbNextBtn?.classList.toggle('swiper-button-disabled', this.thumbSwiper.isEnd);
  }

  // ─── Selling Plan ─────────────────────────────────────────────────
  initSellingPlan() {
    const widget = this.querySelector('[data-selling-plan-widget]');
    if (!widget) return;

    const hiddenInput = widget.querySelector('#pinfo-selling-plan-input');

    const getVariantId = () => {
      const idInput = this.querySelector('input[name="id"]');
      return idInput ? String(idInput.value) : null;
    };

    const syncDisplay = (spId, priceEl, compareEl, badgeEl, freqSelect) => {
      const variantId   = getVariantId();
      const variantData = variantId ? (this.spAllocations.variants[variantId] || null) : null;
      const alloc       = (variantData && spId) ? (variantData.allocations[String(spId)] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && freqSelect) {
        const opt   = freqSelect.options[freqSelect.selectedIndex];
        const badge = opt ? (opt.dataset.badge || '') : '';
        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    };

    widget.querySelectorAll('.sp-widget').forEach(group => {
      const cards         = group.querySelectorAll('.sp-card');
      const subscribeCard = group.querySelector('.sp-card--subscribe');
      const freqSelect    = group.querySelector('[data-frequency-select]');
      const badgeEl       = group.querySelector('[data-badge-display]');
      const priceEl       = group.querySelector('[data-subscribe-price-display]');
      const compareEl     = group.querySelector('[data-subscribe-compare-display]');

      cards.forEach(card => {
        card.addEventListener('click', () => {
          cards.forEach(c => {
            c.classList.remove('active');
            c.querySelector('.sp-hidden-radio').checked = false;
          });
          card.classList.add('active');
          card.querySelector('.sp-hidden-radio').checked = true;

          if (card.dataset.spCard === 'subscribe' && freqSelect) {
            const opt  = freqSelect.options[freqSelect.selectedIndex];
            const spId = opt ? opt.value : '';
            if (hiddenInput) hiddenInput.value = spId;
            syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
          } else {
            if (hiddenInput) hiddenInput.value = '';
          }
        });
      });

      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          const opt  = freqSelect.options[freqSelect.selectedIndex];
          const spId = opt ? opt.value : '';
          if (subscribeCard?.classList.contains('active') && hiddenInput) {
            hiddenInput.value = spId;
          }
          syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
        });

        freqSelect.addEventListener('click', e => e.stopPropagation());

        const initOpt = freqSelect.options[freqSelect.selectedIndex];
        if (initOpt) syncDisplay(initOpt.value, priceEl, compareEl, badgeEl, freqSelect);
      }
    });

    if (hiddenInput) hiddenInput.value = '';
  }

  // ─── Update selling plan prices when variant changes ──────────────
  updateSellingPlanPrices(variantId) {
    const variantData = this.spAllocations.variants[String(variantId)];

    const oneTimePriceEl = this.querySelector('[data-one-time-price]');
    if (oneTimePriceEl && variantData) {
      oneTimePriceEl.textContent = this.formatMoney(variantData.price);
    }

    const planMap = {};
    (this.productJSON?.selling_plan_groups || []).forEach(g =>
      (g.selling_plans || []).forEach(p => { planMap[p.id] = p; })
    );

    this.querySelectorAll('.sp-widget').forEach(groupEl => {
      const freqSelect = groupEl.querySelector('[data-frequency-select]');
      const badgeEl    = groupEl.querySelector('[data-badge-display]');
      const priceEl    = groupEl.querySelector('[data-subscribe-price-display]');
      const compareEl  = groupEl.querySelector('[data-subscribe-compare-display]');
      if (!freqSelect) return;

      const opt    = freqSelect.options[freqSelect.selectedIndex];
      const spId   = opt ? String(opt.value) : null;
      const alloc  = (variantData && spId) ? (variantData.allocations[spId] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && opt && variantData) {
        const plan = planMap[Number(spId)];
        const adj  = plan?.price_adjustments?.[0];
        let badge  = opt.dataset.badge || '';

        if (adj?.value_type === 'price') {
          const saving = variantData.price - adj.value;
          badge = saving > 0 ? 'Save ' + this.formatMoney(saving) : 'Special Price';
          opt.dataset.badge = badge;
        }

        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    });
  }

  // ─── Format money ─────────────────────────────────────────────────
  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    if (window.Shopify?.formatMoney) {
      const fmt = window.Shopify.money_format
        || window.Shopify.moneyFormat
        || window.theme?.moneyFormat
        || '${{amount}}';
      const raw = window.Shopify.formatMoney(cents, fmt);
      return raw.replace(/[.,]00(?!\d)/, '');
    }

    const amount   = cents / 100;
    const hasCents = (amount % 1) !== 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  }

  // ─── Accordion ────────────────────────────────────────────────────
initAccordion() {
  const items = this.querySelectorAll('.accordion-item');
  if (!items.length) return;

  const open = (item) => {
    const content = item.querySelector('.accordion-content');
    const trigger = item.querySelector('.accordion-trigger');
    item.classList.add('accordion__active');
    trigger?.setAttribute('aria-expanded', 'true');
    content.style.maxHeight = content.scrollHeight + 'px';
  };

  const close = (item) => {
    const content = item.querySelector('.accordion-content');
    const trigger = item.querySelector('.accordion-trigger');
    item.classList.remove('accordion__active');
    trigger?.setAttribute('aria-expanded', 'false');
    content.style.maxHeight = null;
  };

  items.forEach(item => {
    item.querySelector('.accordion-trigger')?.addEventListener('click', () => {
      const isOpen = item.classList.contains('accordion__active');
      items.forEach(i => close(i));
      if (!isOpen) open(item);
    });
  });
}

  // ─── Form Bindings ────────────────────────────────────────────────
  bindFormSubmit() {
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;
    form.removeEventListener('submit', this._boundHandleAddToCart);
    this._boundHandleAddToCart = this.handleAddToCart.bind(this);
    form.addEventListener('submit', this._boundHandleAddToCart);
  }

  bindBuyNow() {
    const btn = this.querySelector('.p-info-btn-buy-now');
    if (!btn) return;
    btn.removeEventListener('click', this._boundHandleBuyNow);
    this._boundHandleBuyNow = this.handleBuyNow.bind(this);
    btn.addEventListener('click', this._boundHandleBuyNow);
  }

  handleBuyNow = async (e) => {
    e.preventDefault();

    const btn = e.currentTarget;
    const form = this.getCartAddForm();
    if (!form || !btn || btn.disabled) return;

    this.clearProductError();

    const shouldContinue = await this.confirmProductCompatibilityIfNeeded();
    if (!shouldContinue) return;

    const originalHTML = btn.innerHTML;
    this.setActionButtonLoading(btn, 'Processing...');

    try {
      const data = this.buildCartAddFormData(form, false);
      await this.addItemToCart(data);
      window.location.href = window.routes?.checkout_url || '/checkout';
    } catch (err) {
      console.error('Buy Now failed:', err);
      this.showProductError(this.getProductErrorMessage(err));
      this.restoreActionButton(btn, originalHTML);
    }
  }

  appendSellingPlan(formData) {
    const hiddenInput = this.querySelector('#pinfo-selling-plan-input');
    if (hiddenInput && hiddenInput.value) {
      formData.set('selling_plan', hiddenInput.value);
    } else {
      formData.delete('selling_plan');
    }
  }

  getCartAddForm() {
    return this.querySelector('[data-type="add-to-cart-form"], form[action$="/cart/add"]');
  }

  async getCartDrawer() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart) return null;

    if (cart.matches('cart-drawer') && typeof cart.renderContents !== 'function') {
      await customElements.whenDefined('cart-drawer');
    }

    return typeof cart.renderContents === 'function' ? cart : null;
  }

  buildCartAddFormData(form, includeCartSections = true) {
    const data = new FormData(form);
    const variantInput = form.querySelector('[name="id"]') || this.querySelector('[name="id"]');

    if (!variantInput || !variantInput.value) {
      throw new Error('Please select an available option before adding this product.');
    }

    data.set('id', variantInput.value);

    const qty = this.querySelector('.qty-input, .quantity__input, input[name="quantity"]');
    if (qty) {
      const quantity = Math.max(parseInt(qty.value, 10) || 1, 1);
      data.set('quantity', quantity);
      qty.value = quantity;
    }

    this.appendSellingPlan(data);

    if (includeCartSections && this.cart?.getSectionsToRender) {
      data.append('sections', this.cart.getSectionsToRender().map(section => section.id));
      data.append('sections_url', window.location.pathname);
      this.cart.setActiveElement?.(document.activeElement);
    }

    return data;
  }

  showProductCompatibilityConfirmation(message) {
    return new Promise((resolve) => {
      const popup = document.getElementById('custom-popup');
      const messageBox = document.getElementById('custom-popup-message');
      const yesButton = document.getElementById('popup-yes');
      const noButton = document.getElementById('popup-no');

      if (!popup || !messageBox || !yesButton || !noButton) {
        resolve(true);
        return;
      }

      messageBox.innerHTML = message;
      popup.classList.remove('hidden');

      const finish = (shouldContinue) => {
        popup.classList.add('hidden');
        yesButton.removeEventListener('click', handleYes);
        noButton.removeEventListener('click', handleNo);
        resolve(shouldContinue);
      };

      const handleYes = () => finish(true);
      const handleNo = () => finish(false);

      yesButton.addEventListener('click', handleYes, { once: true });
      noButton.addEventListener('click', handleNo, { once: true });
    });
  }

  async confirmProductCompatibilityIfNeeded() {
    const isSeedProduct = window.productFlags?.isSeedProduct;
    const isPreEmergentProduct = window.productFlags?.isPreEmergentProduct;

    if (!isSeedProduct && !isPreEmergentProduct) return true;

    let cart;
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return true;
      cart = await response.json();
    } catch (error) {
      console.warn('Could not check product compatibility before add to cart:', error);
      return true;
    }

    let message = '';

    for (const item of cart.items || []) {
      const itemTitle = item.title?.toLowerCase() || '';
      const itemType = item.product_type?.toLowerCase() || '';

      if (isSeedProduct && itemTitle.includes('pre-emergent')) {
        message = 'Your cart contains a Pre-Emergent. We do not recommend applying Pre-Emergents and Grass Seed at the same time. Pre-emergent will prevent seed from germinating. Check out our <a href="/products/starter-fertilizer-15-20-10" target="_blank" rel="noopener">Starter Fertilizer</a>, which will boost seed growth. Do you still want to add this Grass Seed?';
        break;
      }

      if (isPreEmergentProduct && itemType === 'seed') {
        message = 'Your cart contains Grass Seed. We do not recommend applying Pre-Emergents and Grass Seed at the same time. Pre-emergent will prevent seed from germinating. Check out our <a href="/products/starter-fertilizer-15-20-10" target="_blank" rel="noopener">Starter Fertilizer</a>, which will boost seed growth. Do you still want to add this Pre-emergent?';
        break;
      }
    }

    if (!message) return true;
    return this.showProductCompatibilityConfirmation(message);
  }

  async addItemToCart(formData) {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    let parsedState = null;
    try {
      parsedState = await response.json();
    } catch (jsonError) {
      if (!response.ok) {
        throw new Error('We could not add this item to your cart. Please try again.');
      }
    }

    if (!response.ok || parsedState?.status) {
      const message = parsedState?.description || parsedState?.message || parsedState?.errors || 'We could not add this item to your cart. Please try again.';
      throw new Error(Array.isArray(message) ? message.join(' ') : String(message));
    }

    return parsedState;
  }

  setActionButtonLoading(button, loadingText = 'Adding...') {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    button.style.opacity = '0.8';

    const textEl = button.querySelector('.atc-text');
    if (textEl) textEl.textContent = loadingText;
    else button.textContent = loadingText;
  }

  restoreActionButton(button, originalHTML) {
    if (!button) return;
    if (originalHTML) button.innerHTML = originalHTML;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('loading');
    button.style.opacity = '1';
  }

  getProductErrorMessage(err) {
    const fallback = 'Something went wrong. Please try again or contact us for help.';
    const message = typeof err === 'string' ? err : err?.message;
    if (!message) return fallback;

    if (/sold out|not enough|unavailable|cannot be added|not available/i.test(message)) {
      return 'This item is currently unavailable or there is not enough inventory for the quantity selected.';
    }

    return message;
  }

  getProductErrorWrapper() {
    let wrapper = this.querySelector('.pinfo-form-wrapper .product-form__error-message-wrapper')
      || this.querySelector('.product-form__error-message-wrapper');

    if (!wrapper) {
      const formWrapper = this.querySelector('.pinfo-form-wrapper');
      if (!formWrapper) return null;
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper pinfo-product-error';
      wrapper.setAttribute('role', 'alert');
      wrapper.hidden = true;
      wrapper.innerHTML = '<span class="product-form__error-message"></span>';
      formWrapper.prepend(wrapper);
    }

    return wrapper;
  }

  showProductError(message) {
    const wrapper = this.getProductErrorWrapper();
    if (!wrapper) return;

    const errorMessage = wrapper.querySelector('.product-form__error-message') || wrapper;
    errorMessage.textContent = message;
    wrapper.hidden = false;
    wrapper.setAttribute('tabindex', '-1');
    wrapper.focus({ preventScroll: true });
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  clearProductError() {
    const wrapper = this.getProductErrorWrapper();
    if (!wrapper) return;

    const errorMessage = wrapper.querySelector('.product-form__error-message');
    if (errorMessage) errorMessage.textContent = '';
    wrapper.hidden = true;
    wrapper.removeAttribute('tabindex');
  }

  handleAddToCart = async (e) => {
    e.preventDefault();

    const btn = this.querySelector('button[type="submit"]');
    const form = this.getCartAddForm();
    if (!form || !btn || btn.disabled) return;

    const originalHTML = btn.innerHTML;
    this.clearProductError();

    const shouldContinue = await this.confirmProductCompatibilityIfNeeded();
    if (!shouldContinue) return;

    this.setActionButtonLoading(btn, 'Adding...');

    this.cart = await this.getCartDrawer();
    let addedSuccessfully = false;

    try {
      const data = this.buildCartAddFormData(form, true);
      const parsedState = await this.addItemToCart(data);
      addedSuccessfully = true;

      if (this.cart?.renderContents) {
        this.cart.renderContents(parsedState);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }

      if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      this.closest('.pqv-wrapper')?.querySelector('.pqv-close-btn')?.click();
    } catch (err) {
      console.error('Add to cart failed:', err);
      this.showProductError(this.getProductErrorMessage(err));
    } finally {
      if (!addedSuccessfully || !this.closest('.pqv-wrapper')) {
        this.restoreActionButton(btn, originalHTML);
      }
    }
  }


  updateURL(url) {
    if (typeof url !== 'string' || !url.startsWith('/')) return;
    if (this.dataset?.updateUrl === 'true') {
      window.history.pushState({}, '', url);
    }
  }


}

document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('product-information')) {
    customElements.define('product-information', ProductInformation);
  }
});
